import useModals from './useModals';

const useApiErrorHandler = () => {
  const { openModal } = useModals();

  const handle = (
    error: any,
    fallbackMessage = 'Ocurrió un error inesperado.'
  ) => {
    const response = error?.response;
    const data = response?.data;
    const headers = response?.headers;
    const status = data?.statusCode || response?.status;
    const rawFieldErrors = data?.fieldErrors || data?.errors;
    const fieldMessages = Array.isArray(rawFieldErrors)
      ? rawFieldErrors
          .map((item: any) => item?.message || item?.defaultMessage)
          .filter(Boolean)
      : rawFieldErrors && typeof rawFieldErrors === 'object'
      ? Object.entries(rawFieldErrors).map(
          ([field, message]) => `${field}: ${String(message)}`
        )
      : [];
    const responseMessage = [data?.message, data?.description, ...fieldMessages]
      .filter(Boolean)
      .join('\n');
    const requestUrl = String(response?.config?.url || '');
    const isAccountsPayableRequest =
      requestUrl.includes('/payables') ||
      requestUrl.includes('/payable/');

    if (status === 403 && isAccountsPayableRequest) {
      openModal('warning', {
        title: 'Módulo no habilitado',
        message:
          'El módulo Cuentas por Pagar no está habilitado en el plan actual',
      });
      return;
    }

    // ============================================================
    // 🧩 CASO 1: Headers personalizados (PRIORIDAD ALTA)
    // ============================================================
    if (headers?.['x-error-title'] || headers?.['x-error-message']) {
      openModal(headers['x-error-type'] || 'error', {
        title: headers['x-error-title'] || 'Error',
        message: headers['x-error-message'] || fallbackMessage,
      });
      return;
    }

    // ============================================================
    // 🧩 CASO 2: Respuesta JSON estructurada
    // ============================================================
    if (status) {
      const httpType = getModalTypeByStatus(status);
      const message = responseMessage || fallbackMessage;

      openModal(data?.type || httpType, {
        title: data?.title || getTitleByStatus(status),
        message,
      });

      return;
    }

    // ============================================================
    // ❌ Caso 3: Error inesperado
    // ============================================================
    console.error('❌ Error API:', error);

    openModal('error', {
      title: 'Error de conexión',
      message:
        'No se pudo establecer comunicación con el servidor. Verifica tu conexión a internet o intenta nuevamente.',
    });
  };

  /**
   * 🧠 Devuelve el título sugerido según el código HTTP
   */
  const getTitleByStatus = (status: number): string => {
    const titles: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado',
      403: 'Acceso denegado',
      404: 'Recurso no encontrado',
      405: 'Método no permitido',
      408: 'Tiempo de espera agotado',
      409: 'Conflicto detectado',
      410: 'Recurso eliminado',
      422: 'Entidad no procesable',
      429: 'Demasiadas solicitudes',
      500: 'Error interno del servidor',
      501: 'Funcionalidad no implementada',
      502: 'Puerta de enlace incorrecta',
      503: 'Servicio no disponible',
      504: 'Tiempo de espera del servidor agotado',
    };

    // Si existe un título personalizado, lo devolvemos
    if (titles[status]) return titles[status];

    // Rangos genéricos
    if (status >= 400 && status < 500) return 'Error en la solicitud';
    if (status >= 500) return 'Error del servidor';
    return 'Error desconocido';
  };

  /**
   * 🎨 Define el tipo de modal según la categoría HTTP
   */
  const getModalTypeByStatus = (status: number): string => {
    if (status >= 200 && status < 300) return 'success';
    if (status === 401 || status === 403) return 'warning';
    if (status === 409) return 'warning'; // conflicto de datos o duplicado
    if (status >= 400 && status < 500) return 'error';
    if (status >= 500) return 'error';
    return 'error';
  };

  return { handle };
};

export default useApiErrorHandler;
